import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { MAIN_MENU, MENU_GROUPS, getMenuIcon, isItemAllowedForState, isMenuItemLocked, isPremiumItemGranted, resolveMenuHref } from "@/config/mainMenu";
import { getNavLabel } from "@/config/navigation";
import { isPathHidden } from "@/lib/navigationVisibility";
import { getLocalizedPath, stripLanguageFromPath } from "@/lib/i18nRoutes";
import { useTheme } from "@/lib/ThemeProvider";

function isHidden(href, hiddenPages) {
  if (!href) return false;
  return isPathHidden(href, hiddenPages) || isPathHidden(stripLanguageFromPath(href), hiddenPages);
}

function normalizePath(path) {
  const normalized = stripLanguageFromPath(path || "/").replace(/\/+$/, "");
  return normalized || "/";
}

function isActivePath(current, href) {
  if (!href) return false;
  return normalizePath(current) === normalizePath(href);
}

const premiumLabels = {
  en: "Premium", he: "פרימיום", es: "Premium", it: "Premium", fr: "Premium", de: "Premium", pt: "Premium", ar: "بريميوم", el: "Premium", ru: "Премиум", tr: "Premium", zh: "高级",
};

function SmartMenuLink({ item, href, active, locked, collapsed, mode, lang, onNavigate, onPremiumClick }) {
  const Icon = getMenuIcon(item.icon);
  const label = getNavLabel(item.labelKey, lang);
  const target = href;
  const { isDark } = useTheme();
  const publicLinkStyle = mode === "public" ? { color: active && !locked ? "#ffffff" : isDark ? "#ecfeff" : "#0f172a" } : undefined;

  return (
    <Link
      to={target}
      title={label}
      aria-label={label}
      onPointerUp={(event) => event.currentTarget.blur()}
      onPointerCancel={(event) => event.currentTarget.blur()}
      aria-current={active && !locked ? "page" : undefined}
      data-menu-active={active && !locked ? "true" : undefined}
      style={publicLinkStyle}
      onClick={(event) => {
        if (locked) {
          event.preventDefault();
          onPremiumClick?.();
        }
        event.currentTarget.blur();
        onNavigate?.();
      }}
      className={cn(
        "group flex min-h-12 items-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold leading-tight transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 active:scale-[0.99]",
        collapsed && "justify-center px-2",
        locked && "cursor-pointer opacity-90",
        active && !locked
          ? "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg shadow-primary/20"
          : mode === "app"
            ? "text-slate-700 hover:bg-primary/10 hover:text-slate-950 dark:text-cyan-50 dark:hover:bg-white/10 dark:hover:text-white"
            : "text-slate-700 hover:bg-primary/10 hover:text-slate-950 dark:text-cyan-50 dark:hover:bg-white/10 dark:hover:text-white"
      )}
    >
      <Icon className="h-5 w-5 shrink-0 text-current" />
      {!collapsed && <span className={cn("min-w-0 flex-1 truncate text-current", lang === "he" || lang === "ar" ? "text-right" : "text-left")}>{label}</span>}
      {!collapsed && locked && <Lock className="h-4 w-4 shrink-0 text-warning" />}
      {!collapsed && item.tier === "premium" && !locked && <span className="ml-auto shrink-0 rounded-full border border-primary/30 bg-white/80 px-2 py-0.5 text-[10px] font-black text-primary">{premiumLabels[lang] || premiumLabels.en}</span>}
    </Link>
  );
}

function SmartMenu({ mode = "public", variant = "stack", lang = "en", userState, collapsed = false, onNavigate, onPremiumClick }) {
  const location = useLocation();
  const { isDark } = useTheme();
  const [hiddenPages, setHiddenPages] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("hiddenPages") || "[]"); } catch { return []; }
  });

  React.useEffect(() => {
    const syncHiddenPages = () => {
      try { setHiddenPages(JSON.parse(localStorage.getItem("hiddenPages") || "[]")); } catch { setHiddenPages([]); }
    };
    window.addEventListener("hiddenPagesChanged", syncHiddenPages);
    return () => window.removeEventListener("hiddenPagesChanged", syncHiddenPages);
  }, []);

  const groupedItems = React.useMemo(() => {
    const visible = MAIN_MENU.map((item) => {
      const baseHref = resolveMenuHref(item, mode, lang);
      const localizedAppKeys = ["dashboard", "boats", "maintenance", "trips", "logbook", "voyageHistory", "metrics", "offline", "settings", "billing", "help"];
      const appLocalizedHref = localizedAppKeys.includes(item.key) ? getLocalizedPath(item.key, lang) : null;
      const localizedAppHref = appLocalizedHref && appLocalizedHref !== "/404" ? appLocalizedHref : item.appHref;
      const href = mode === "public" && userState?.isLoggedIn && item.zone === "app" && item.appHref
        ? localizedAppHref
        : mode === "public" && isPremiumItemGranted(item, userState) && item.appHref
          ? localizedAppHref
          : baseHref;
      if (variant !== "top" && item.key === "settings") return null;
      if (variant !== "top" && mode === "public" && item.key === "advertise") return null;
      if (!href || !isItemAllowedForState(item, userState, mode) || isHidden(href, hiddenPages)) return null;
      return { ...item, href, locked: isMenuItemLocked(item, userState) || (mode === "public" && item.zone === "app" && !userState?.isLoggedIn) };
    }).filter(Boolean);

    return MENU_GROUPS.map((group) => ({
      ...group,
      items: visible.filter((item) => item.group === group.key),
    })).filter((group) => group.items.length > 0);
  }, [mode, lang, userState, hiddenPages]);

  if (variant === "top") {
    return (
      <nav className="flex items-center gap-1 overflow-x-auto" aria-label="Top navigation">
        {groupedItems.flatMap((group) => group.items).map((item) => (
          <SmartMenuLink
            key={`${item.key}-${item.href}`}
            item={item}
            href={item.href}
            locked={item.locked}
            active={isActivePath(location.pathname, item.href)}
            collapsed={false}
            mode={mode}
            lang={lang}
            onNavigate={onNavigate}
            onPremiumClick={onPremiumClick}
          />
        ))}
      </nav>
    );
  }

  const regularGroups = groupedItems.filter((group) => group.key !== "admin");
  const adminGroup = groupedItems.find((group) => group.key === "admin");
  const orderedGroups = adminGroup ? [...regularGroups, adminGroup] : regularGroups;

  return (
    <nav className="flex min-h-full flex-col gap-4 pb-4" aria-label={mode === "app" ? "App navigation" : "Public navigation"}>
      {orderedGroups.map((group) => (
        <div key={group.key} className={cn("space-y-1", group.key === "admin" && "mt-auto border-t border-warning/30 pt-4")}>
          {!collapsed && (
            <p
              className={cn(
                "px-3 pt-2 text-[10px] font-black uppercase tracking-[0.22em]",
                mode === "app" ? "text-slate-500 dark:text-cyan-100/80" : "text-slate-600 dark:text-cyan-100/90",
                group.key === "admin" && "text-warning"
              )}
              style={mode === "public" ? { color: group.key === "admin" ? "#f59e0b" : isDark ? "#cffafe" : "#334155" } : undefined}
            >
              {getNavLabel(group.labelKey, lang)}
            </p>
          )}
          {group.items.map((item) => (
            <SmartMenuLink
              key={`${item.key}-${item.href}`}
              item={item}
              href={item.href}
              locked={item.locked}
              active={isActivePath(location.pathname, item.href)}
              collapsed={collapsed}
              mode={mode}
              lang={lang}
              onNavigate={onNavigate}
              onPremiumClick={onPremiumClick}
            />
          ))}
        </div>
      ))}
    </nav>
  );
}

export default React.memo(SmartMenu);